import { Link } from "react-router-dom";
import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import { gql } from "graphql-tag";
import {
  ProcedureCycle,
  ProcessCycle,
  StepCycle,
  WorkPriority,
  WorkStatus,
} from "../../gql/graphql"; // Assuming ProcessCycle is the correct type
import { ProcessInfo } from "../../shared/interfaces";
import {
  Alert,
  Button,
  Card,
  Drawer,
  List,
  Loader,
  Modal,
  Table,
  Stack,
  Group,
  Text,
  Checkbox,
  Center,
} from "@mantine/core";
import { IconPencil, IconCircleXFilled } from "@tabler/icons-react";
// Removed the import for "@mantine/icons" due to the error

import React, { useCallback, useEffect, useRef, useState } from "react";
// import CycleTree from "../misc/CycleTree";
import ProcessCycleForm from "../forms/ProcessCycleForm";
import ReactDOM from "react-dom";
import { CYCLE_DETAILS } from "../../shared/fragments";
import { SelectProcessCycleStatus } from "../../components/SelectStatus";
import {
  CREATE_PROCESS_CYCLE,
  UPDATE_PROCESS_CYCLE,
  DELETE_PROCESS_CYCLE,
} from "../../shared/mutations";
// import { format, formatDistanceToNow, isPast, isToday } from "date-fns";
import { isMobile } from "react-device-detect";
import { DataTable } from "mantine-datatable";
import _ from "lodash";
import { StatusField } from "../../components/fields/StatusField";
import { PriorityField } from "../../components/fields/PriorityField";
import { ProjectView } from "../misc/ProjectView";
import { DeadlineField } from "../../components/fields/DeadlneField";

const PROCESS_CYCLE_PAGE_SIZE = 50;
const PULL_LOAD_THRESHOLD_PX = 64;

function useLoadMoreOnScroll(
  containerRef: React.RefObject<HTMLDivElement | null>,
  sentinelRef: React.RefObject<HTMLDivElement | null>,
  options: {
    hasNextPage: boolean;
    loadingMore: boolean;
    onLoadMore: () => void;
    /** Re-run listeners when switching mobile/desktop so ref targets the mounted node */
    layoutKey: string;
  }
) {
  const { hasNextPage, loadingMore, onLoadMore, layoutKey } = options;
  const [pullPixels, setPullPixels] = useState(0);
  const pullAmountRef = useRef(0);

  useEffect(() => {
    const root = containerRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    const io = new IntersectionObserver(
      (entries) => {
        const hit = entries[0]?.isIntersecting;
        if (hit && hasNextPage && !loadingMore) onLoadMore();
      },
      { root, rootMargin: "100px", threshold: 0 }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [
    containerRef,
    sentinelRef,
    hasNextPage,
    loadingMore,
    onLoadMore,
    layoutKey,
  ]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let pullActive = false;
    let startY = 0;

    const onTouchStart = (e: TouchEvent) => {
      if (!hasNextPage || loadingMore) return;
      if (el.scrollTop > 1) return;
      pullActive = true;
      startY = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pullActive || !hasNextPage || loadingMore) return;
      if (el.scrollTop > 1) {
        pullActive = false;
        pullAmountRef.current = 0;
        setPullPixels(0);
        return;
      }
      const dy = e.touches[0].clientY - startY;
      if (dy > 0) {
        e.preventDefault();
        const v = Math.min(dy * 0.45, 100);
        pullAmountRef.current = v;
        setPullPixels(v);
      } else {
        pullAmountRef.current = 0;
        setPullPixels(0);
      }
    };

    const endPull = () => {
      if (!pullActive) return;
      pullActive = false;
      const v = pullAmountRef.current;
      pullAmountRef.current = 0;
      setPullPixels(0);
      if (v >= PULL_LOAD_THRESHOLD_PX && hasNextPage && !loadingMore) {
        onLoadMore();
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", endPull);
    el.addEventListener("touchcancel", endPull);

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", endPull);
      el.removeEventListener("touchcancel", endPull);
    };
  }, [containerRef, hasNextPage, loadingMore, onLoadMore, layoutKey]);

  return { pullPixels };
}

const GET_PROCESS_CYCLES_INFO = gql`
  ${CYCLE_DETAILS}
  query getProcessCyclesByProcess($id: ID!, $first: Int, $after: String) {
    processCyclesByProcess(id: $id, first: $first, after: $after) {
      items {
        ...CycleDetails
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const GET_TENANT_CYCLES_INFO = gql`
  ${CYCLE_DETAILS}
  query getProcessCyclesByTenant($id: ID!, $first: Int, $after: String) {
    processCyclesByTenant(id: $id, first: $first, after: $after) {
      items {
        ...CycleDetails
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const GET_SPACE_CYCLES_INFO = gql`
  ${CYCLE_DETAILS}
  query getProcessCyclesBySpace($id: ID!, $first: Int, $after: String) {
    processCyclesBySpace(id: $id, first: $first, after: $after) {
      items {
        ...CycleDetails
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export function useCreateProcessCycleMutation() {
  return useMutation(CREATE_PROCESS_CYCLE);
}

export const UPDATE_PROCESS_CYCLES_STATUS = gql`
  mutation updateProcessCyclesStatus($ids: [ID!]!, $status: WorkStatus!) {
    updateProcessCyclesStatus(ids: $ids, status: $status) {
      id
      status
    }
  }
`;
export function useUpdateProcessCyclesStatusMutation() {
  return useMutation(UPDATE_PROCESS_CYCLES_STATUS);
}

// Add cyclesBy type prop with values "PROCESS" or "SPACE"
interface ProjectTableProps {
  cyclesBy: "PROCESS" | "SPACE" | "TENANT";
  id: string | null;
}

const ProjectTable: React.FC<ProjectTableProps> = ({ id, cyclesBy }) => {
  const ModalStates = {
    OFF: "OFF",
    ADD: "ADD",
    EDIT: "EDIT",
  };

  const [hoveredRow, setHoveredRow] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState("");
  const [selectedCycleId, setSelectedCycleId] = useState("");
  const [selectedCycleStatus, setSelectedCycleStatus] = useState("");
  const [cycleTreeData, setCycleTreeData] = useState<string | null>(null);
  const [cycleTreeChecked, setCycleTreeChecked] = useState<string[]>([]);
  const [cycleTreeExpanded, setCycleTreeExpanded] = useState<string[]>([]);
  const [modalState, setModalState] = useState(ModalStates.OFF);
  const [selectedRow, setSelectedRow] = useState<ProcessInfo | null>(null);
  const [tableData, setTableData] = useState<ProcessInfo[]>([]);
  const [cyclesData, setCyclesData] = useState<ProcessCycle[]>([]);
  function useUpdateProcessCycleMutation() {
    return useMutation(UPDATE_PROCESS_CYCLE);
  }
  const [updateProcessCycle, { data: updateProcessCycleData }] =
    useUpdateProcessCycleMutation();
  const [createProcessCycle, { data: createProcessCycleData }] =
    useCreateProcessCycleMutation();
  const [updateProcessCyclesStatus] = useUpdateProcessCyclesStatusMutation();

  function useDeleteProcessCycleMutation() {
    return useMutation(DELETE_PROCESS_CYCLE);
  }

  const [deleteProcessCycle] = useDeleteProcessCycleMutation();

  const listQuery =
    cyclesBy === "PROCESS"
      ? GET_PROCESS_CYCLES_INFO
      : cyclesBy === "TENANT"
      ? GET_TENANT_CYCLES_INFO
      : GET_SPACE_CYCLES_INFO;

  const pageRootKey =
    cyclesBy === "PROCESS"
      ? "processCyclesByProcess"
      : cyclesBy === "TENANT"
      ? "processCyclesByTenant"
      : "processCyclesBySpace";

  const { loading, error, data, refetch, fetchMore, networkStatus } = useQuery(
    listQuery,
    {
      variables: { id, first: PROCESS_CYCLE_PAGE_SIZE },
      skip: !id,
      fetchPolicy: "network-only",
    }
  );

  const listPageForHeader = data?.[
    pageRootKey as keyof typeof data
  ] as { pageInfo?: { hasNextPage?: boolean | null } } | undefined;
  const hasNextPage = Boolean(listPageForHeader?.pageInfo?.hasNextPage);
  const loadingMore = networkStatus === NetworkStatus.fetchMore;
  const fetchMoreInFlightRef = useRef(false);

  const handleLoadMore = useCallback(() => {
    if (
      networkStatus === NetworkStatus.fetchMore ||
      fetchMoreInFlightRef.current
    ) {
      return;
    }
    const root = data?.[pageRootKey as keyof typeof data] as
      | {
          items?: ProcessCycle[] | null;
          pageInfo?: { hasNextPage?: boolean | null; endCursor?: string | null };
        }
      | undefined;
    const endCursor = root?.pageInfo?.endCursor;
    if (!root?.pageInfo?.hasNextPage || !endCursor || !id) return;
    fetchMoreInFlightRef.current = true;
    fetchMore({
      variables: {
        id,
        first: PROCESS_CYCLE_PAGE_SIZE,
        after: endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const pk = pageRootKey as keyof typeof prev;
        const prevRoot = prev[pk] as {
          items?: ProcessCycle[] | null;
          pageInfo?: { hasNextPage?: boolean | null; endCursor?: string | null };
        };
        const nextRoot = fetchMoreResult[pk] as typeof prevRoot;
        return {
          ...prev,
          [pk]: {
            ...nextRoot,
            items: [
              ...(prevRoot?.items ?? []),
              ...(nextRoot?.items ?? []),
            ],
            pageInfo: nextRoot.pageInfo,
          },
        };
      },
    }).finally(() => {
      fetchMoreInFlightRef.current = false;
    });
  }, [data, fetchMore, id, networkStatus, pageRootKey]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const { pullPixels } = useLoadMoreOnScroll(scrollContainerRef, loadMoreSentinelRef, {
    hasNextPage,
    loadingMore,
    onLoadMore: handleLoadMore,
    layoutKey: isMobile ? "m" : "d",
  });

  useEffect(() => {
    if (!loading && data) {
      const page =
        cyclesBy === "PROCESS"
          ? data?.processCyclesByProcess
          : cyclesBy === "TENANT"
          ? data?.processCyclesByTenant
          : data?.processCyclesBySpace;
      const result = page?.items;
      setTableData(
        result?.map((cycle: ProcessCycle) => {
          // Find the first procedure cycle where the status is not "Skipped" or "Done"
          const currentProcedureCycle = cycle.procedureCycles?.find(
            (procedureCycle: ProcedureCycle | null | undefined) =>
              procedureCycle?.status !== "Skipped" &&
              procedureCycle?.status !== "Done"
          );

          return {
            key: cycle.id,
            cycle: cycle.title,
            status: cycle.status,
            priority: cycle.priority,
            description: cycle.description || "",
            deadline: cycle.deadline || "",
            process: cycle.process?.title || "",
            spaceTitle: cycle.process?.spaceTitle || "",
            spaceId: cycle.process?.spaceId || "",
            processId: cycle.process?.id || "",
            // Add the currentProcedure property
            currentProcedure: currentProcedureCycle?.procedure?.title || "",
            selected: false,
            updatedAt: cycle.updatedAt || "",
          };
        }) ?? []
      );
      setCyclesData(result || []);
      if (selectedCycleId && result) {
        result.map((cycle: ProcessCycle) => {
          if (cycle.id === selectedCycleId) {
            setSelectedCycleStatus(cycle.status || "");
          }
        });
      }
      console.log("data from processCycles", data);
    }
  }, [data]);

  if (loading && !data) return <p>Loading...</p>;

  if (error) {
    if (
      error.networkError &&
      "statusCode" in error.networkError &&
      error.networkError.statusCode === 401
    ) {
      window.location.reload();
      return null;
    }
    return <p>Error: {error.message}</p>;
  }

  const handleCancel = () => {
    setModalState(ModalStates.OFF);
  };

  const handleEditCycle = (entity: any) => {
    setSelectedRow(entity);
    setModalState(ModalStates.EDIT);
  };

  const handleSaveProcessCycle = async (processCycle: any) => {
    if (modalState === ModalStates.ADD) {
      try {
        const result = await createProcessCycle({
          variables: {
            title: processCycle.title,
            description: processCycle.description,
            processId: id,
            status: WorkStatus.ToDo,
            priority: processCycle.priority,
            input: processCycle.input,
          },
        });

        const currentProcedureCycle =
          result.data.createProcessCycle.procedureCycles?.find(
            (procedureCycle: ProcedureCycle | null | undefined) =>
              procedureCycle?.status !== "Skipped" &&
              procedureCycle?.status !== "Done"
          );
        const newTableData: ProcessInfo = {
          key: result.data.createProcessCycle.id,
          cycle: result.data.createProcessCycle.title,
          status: result.data.createProcessCycle.status,
          process: result.data.createProcessCycle.process.title,
          priority: result.data.createProcessCycle.priority,
          deadline: result.data.createProcessCycle.deadline,
          description: result.data.createProcessCycle.description,
          processId: result.data.createProcessCycle.process.id,
          currentProcedure: currentProcedureCycle?.procedure?.title || "",
          selected: false,
          updatedAt: result.data.createProcessCycle.updatedAt || "",
        };

        setTableData([...tableData, newTableData]);
        setCyclesData([...cyclesData, result.data.createProcessCycle]);

        // Handle the result if needed
        console.log("Mutation result:", result);
      } catch (error) {
        // Handle the error if the mutation fails
        console.error("Mutation error:", error);
      }
    } else if (modalState === ModalStates.EDIT) {
      try {
        const result = await updateProcessCycle({
          variables: {
            id: processCycle.id, // Provide the processCycle ID to identify which processCycle to update
            title: processCycle.title,
            description: processCycle.description,
            priority: processCycle.priority,
            deadline: processCycle.deadline,
            status: processCycle.status,
          },
        });
        const currentProcedureCycle =
          result.data.updateProcessCycle.procedureCycles?.find(
            (procedureCycle: ProcedureCycle | null | undefined) =>
              procedureCycle?.status !== "Skipped" &&
              procedureCycle?.status !== "Done"
          );
        setTableData((tabbleData) =>
          tableData.map((item) =>
            item.key === processCycle.id
              ? {
                  key: result.data.updateProcessCycle.id,
                  id: result.data.updateProcessCycle.id,
                  cycle: result.data.updateProcessCycle.title,
                  process: result.data.updateProcessCycle.process.title,
                  priority: result.data.updateProcessCycle.priority,
                  deadline: result.data.updateProcessCycle.deadline,
                  status: result.data.updateProcessCycle.status,
                  processId: result.data.updateProcessCycle.process.id,
                  description: result.data.updateProcessCycle.description,
                  currentProcedure:
                    currentProcedureCycle?.procedure?.title || "",
                  selected: false,
                  updatedAt: result.data.updateProcessCycle.updatedAt || "",
                }
              : item
          )
        );
        setCyclesData((cyclesData) =>
          cyclesData.map((item) =>
            item.id === result.data.updateProcessCycle.id
              ? result.data.updateProcessCycle
              : item
          )
        );
        // Handle the result if needed
        console.log("Mutation result:", result);
      } catch (error) {
        // Handle the error if the mutation fails
        console.error("Mutation error:", error);
      }
    }
    setModalState(ModalStates.OFF);
  };

  const handleDeleteProcessCycle = async () => {
    if (selectedRow?.key) {
      try {
        await deleteProcessCycle({
          variables: {
            id: selectedRow.key,
          },
        });
        setTableData(tableData.filter((row) => row.key !== selectedRow.key));
        setCyclesData(
          cyclesData.filter((cycle) => cycle.id !== selectedRow.key)
        );
        handleCancel();
      } catch (error) {
        console.error("Delete mutation error:", error);
      }
    }
  };

  const handleMouseEnter = (record: any) => {
    setHoveredRow(record.key);
  };

  const handleMouseLeave = () => {
    setHoveredRow(null);
  };

  const updateCycle = async (record: any) => {
    try {
      const result = await updateProcessCycle({
        variables: {
          id: record.key, // Provide the processCycle ID to identify which processCycle to update
          title: record.cycle,
          status: record.status as WorkStatus,
          priority: record.priority as WorkPriority,
          deadliine: record.deadline,
        },
      });
    } catch (error) {
      // Handle the error here
      console.error(error);
    }
  };

  const setCycleStatus = (record: any, status: WorkStatus) => {
    record.status = status;
    updateCycle(record);
  };

  const onSelectProcessCycleStatus = async (ids: string[], status: string) => {
    try {
      console.log("onSelectStepStatus", status);
      await updateProcessCyclesStatus({
        variables: {
          ids: ids,
          status: status.replace(" ", "_"),
        },
      });
      refetch && refetch();
    } catch (error) {
      console.error(
        "Error updating step cycles" + ids + " status to " + status,
        error
      );
    }
  };

  // Filtering data
  let uniqueStatus: string[] = [];
  let uniqueProcess: String[] = [];

  cyclesData?.map((cycle: ProcessCycle) => {
    if (uniqueStatus.indexOf(cycle.status as string) === -1) {
      uniqueStatus.push(cycle.status as string);
    }
    if (uniqueProcess.indexOf(cycle.process?.title as string) === -1) {
      uniqueProcess.push(cycle.process?.title as string);
    }
  });

  const columns = [
    {
      accessor: "selection",
      title: (
        <div
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const allSelected = tableData.every((item) => item.selected);
            setTableData(
              tableData.map((item) => ({
                ...item,
                selected: !allSelected,
              }))
            );
          }}
        >
          <Checkbox
            className="mantine-projecttable-Checkbox-input"
            checked={
              tableData.length > 0 && tableData.every((item) => item.selected)
            }
            indeterminate={
              tableData.some((item) => item.selected) &&
              !tableData.every((item) => item.selected)
            }
            readOnly
            color="#9E9E9E"
            size="md"
          />
        </div>
      ),
      width: 40,
      render: (record: any) => (
        <div
          // style={{ width: "100%", height: "100%", cursor: "pointer" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setTableData(
              tableData.map((item) =>
                item.key === record.key
                  ? { ...item, selected: !item.selected }
                  : item
              )
            );
          }}
        >
          <Checkbox
            className="mantine-projecttable-Checkbox-input"
            checked={record.selected || false}
            readOnly
            color="#9E9E9E"
            size="md"
            height={14}
            width={14}
          />
        </div>
      ),
    },
    {
      accessor: "updatedAt",
      hidden: true,
    },
    {
      accessor: "spaceTitle",
      title: "Space",
      hidden: cyclesBy !== "TENANT" || isMobile,
      ellipsis: true,
    },
    {
      accessor: "process",
      title: "Process",
      render: (record: any) => (
        <Link to={`/process/${record.processId}`}>{record.process}</Link>
      ),
      hidden: cyclesBy === "PROCESS",
      ellipsis: true,
    },
    {
      accessor: "cycle",
      title: "Project",
      ellipsis: true,
      width: 400,
    },
    {
      accessor: "status",
      title: "Status",
      render: (record: any) => (
        <StatusField
          status={record.status}
          currentProcedure={record.currentProcedure}
        />
      ),
      noWrap: true,
      width: 100,
    },
    {
      accessor: "priority",
      title: "Priority",
      noWrap: true,
      render: (record: any) => <PriorityField priority={record.priority} />,
      width: 150,
    },
    {
      accessor: "deadline",
      title: "Deadline",
      noWrap: true,
      render: (record: any) => <DeadlineField deadline={record.deadline} />,
    },
    {
      accessor: "actions",
      title: "",
      noWrap: true,

      render: (record: any) => (
        <div
          style={{ height: "22px", display: "flex", alignItems: "center" }}
          onClick={(e) => e.stopPropagation()}
        >
          {(hoveredRow === record.key || isMobile || true) && (
            <div>
              <Button
                variant="subtle"
                leftSection={<IconPencil size={16} />}
                onClick={(e) => {
                  handleEditCycle(record);
                  e.stopPropagation();
                }}
              />
            </div>
          )}
        </div>
      ),
    },
  ];

  const handleAddCycle = () => {
    setModalState(ModalStates.ADD);
  };

  const TableHeader: React.FC<{
    headerTitle: string;
    hasNextPage?: boolean;
  }> = ({ headerTitle, hasNextPage = false }) => {
    return (
      <Stack gap="xs" mb="xs" ml="10px">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="title">
            {headerTitle}
            {isMobile && (
              <span>
                &nbsp; (
                {
                  tableData.filter(
                    (record) => !"Done;Skipped;".includes(record.status)
                  ).length
                }
                )
              </span>
            )}
          </div>
          {cyclesBy === "PROCESS" && (
            <Button size="xs" onClick={handleAddCycle}>
              Add Project
            </Button>
          )}
        </div>
        {hasNextPage && (
          <Alert color="gray" title="More projects available">
            Newest first. Pull down (touch) or scroll to the bottom to load older
            projects.
          </Alert>
        )}
      </Stack>
    );
  };

  function sortData(data: ProcessInfo[]) {
    return _.orderBy(
      data,
      [
        "updatedAt",
        (item) => (item.priority === "Critical" ? 0 : 1),
        (item) => (item.deadline ? 0 : 1),
        "deadline",
        (item) =>
          item.priority === "Critical"
            ? 1
            : item.priority === "High"
            ? 2
            : item.priority === "Medium"
            ? 3
            : item.priority === "Low"
            ? 4
            : 5,
      ],
      ["desc", "asc", "asc", "asc"]
    );
  }

  function openDrawer(record: any) {
    ReactDOM.unstable_batchedUpdates(() => {
      const key = record.key;

      setCycleTreeData(key);

      console.log("data", data, "key", key);

      // create a flat array of checked keys, based on step cycle id's where status is "Done"
      const checked: string[] = cyclesData
        .filter((cycle: ProcessCycle) => cycle.id === key)
        .flatMap((cycle: ProcessCycle) =>
          (cycle.procedureCycles || []).flatMap(
            (procedureCycle: ProcedureCycle | null | undefined) => {
              if (!procedureCycle) return []; // Handle null and undefined values

              return (procedureCycle.stepCycles || []).map(
                (stepCycle: StepCycle | null | undefined) => {
                  if (!stepCycle) return null; // Handle null and undefined values

                  return stepCycle.status === WorkStatus.Done
                    ? stepCycle.id
                    : null;
                }
              );
            }
          )
        )
        .filter((id: string | null) => id !== null) as string[]; // Filter out null values and cast to string[]
      setCycleTreeChecked(checked);

      let firstAllToDo = true;

      const expanded: string[] = cyclesData
        .filter((cycle: ProcessCycle) => cycle.id === key)
        .flatMap((cycle: ProcessCycle) => {
          return (cycle.procedureCycles || []).flatMap(
            (
              procedureCycle: ProcedureCycle | null | undefined,
              index: number,
              array: (ProcedureCycle | null | undefined)[]
            ) => {
              if (!procedureCycle) return []; // Handle null and undefined values

              // Check if any step cycle is not in status Skip or Done
              const hasActiveStep = (procedureCycle.stepCycles || []).some(
                (stepCycle: StepCycle | null | undefined) => {
                  if (!stepCycle) return false; // Handle null and undefined values
                  return (
                    stepCycle.status !== WorkStatus.Skipped &&
                    stepCycle.status !== WorkStatus.Done
                  );
                }
              );

              // Check if all step cycles are in status ToDo
              const allToDo = (procedureCycle.stepCycles || []).every(
                (stepCycle: StepCycle | null | undefined) => {
                  if (!stepCycle) return false; // Handle null and undefined values
                  return stepCycle.status === WorkStatus.ToDo;
                }
              );

              // Include the first procedure cycle or if it has at least one active step cycle
              if ((allToDo && firstAllToDo) || (hasActiveStep && !allToDo)) {
                // Update firstAllToDo
                firstAllToDo = false;
                return procedureCycle.id || null;
              }

              return null;
            }
          );
        })
        .filter((id: string | null) => id !== null) as string[]; // Filter out null values and cast to string[]
      setCycleTreeExpanded(expanded);
      setDrawerTitle(record.cycle);
      setSelectedCycleId(record.key);
      setSelectedCycleStatus(record.status);
      setDrawerVisible(true);
    });
  }

  return (
    <div style={{ height: "calc(100vh - 100px)" }}>
      <Group
        align="flex-start"
        style={{
          height: "100%",
          width: "100%",
          flexWrap: "nowrap",
          display: "flex",
        }}
      >
        <div
          style={{
            flexBasis: "60%",
            flexGrow: 1,
            minWidth: 0,
            maxWidth: "60%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {isMobile && (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <div
                ref={scrollContainerRef}
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {pullPixels > 0 && (
                  <div
                    style={{
                      height: pullPixels,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Loader size="sm" type="oval" />
                  </div>
                )}
                {TableHeader({
                  headerTitle:
                    cyclesBy === "PROCESS"
                      ? "Process Projects"
                      : cyclesBy === "TENANT"
                      ? "All Projects"
                      : "Space Projects",
                  hasNextPage,
                })}
                <List>
                  {sortData(tableData)
                    .filter((record) => !"Done;Skipped;".includes(record.status))
                    .map((record: ProcessInfo) => {
                      return (
                        <List.Item key={record.key} onClick={() => openDrawer(record)}>
                          <Card>
                            <Stack>
                              <Group justify="space-between">
                                <Text fw={500}>{record.cycle}</Text>
                                <Button
                                  variant="subtle"
                                  leftSection={<IconPencil size={16} />}
                                  onClick={(e) => {
                                    handleEditCycle(record);
                                    e.stopPropagation();
                                  }}
                                />
                              </Group>
                              <Text>
                                <strong>Status:</strong>{" "}
                                {record.status.replace("_", " ") +
                                  ("In_Progress;To_Do".includes(record.status) &&
                                  record.currentProcedure
                                    ? " (" + record.currentProcedure + ")"
                                    : "")}
                              </Text>
                              <Text>
                                <strong>Priority:</strong> {record.priority}
                              </Text>
                              {cyclesBy !== "PROCESS" && (
                                <Text>
                                  <strong>Process:</strong>{" "}
                                  <Link to={`/process/${record.processId}`}>
                                    {record.process}
                                  </Link>
                                </Text>
                              )}
                              {record.deadline && (
                                <Text>
                                  <strong>Deadline:</strong>{" "}
                                  <DeadlineField deadline={record.deadline} />
                                </Text>
                              )}
                            </Stack>
                          </Card>
                        </List.Item>
                      );
                    })}
                </List>
                <div
                  ref={loadMoreSentinelRef}
                  style={{ height: 1, width: "100%" }}
                  aria-hidden
                />
                {loadingMore && (
                  <Center py="sm">
                    <Loader size="sm" type="oval" />
                  </Center>
                )}
              </div>
            </div>
          )}
          {!isMobile && (
            <div
              className="mantine-wrapper"
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <TableHeader
                headerTitle={
                  cyclesBy === "PROCESS"
                    ? "Process Projects"
                    : cyclesBy === "TENANT"
                    ? "All Projects"
                    : "Space Projects"
                }
                hasNextPage={hasNextPage}
              />
              <div
                ref={scrollContainerRef}
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  height: "100%",
                }}
              >
                {pullPixels > 0 && (
                  <div
                    style={{
                      height: pullPixels,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Loader size="sm" type="oval" />
                  </div>
                )}
                <DataTable
                  records={sortData(tableData)}
                  columns={columns}
                  onRowClick={({ record, index, event }) => {
                    setSelectedRow(record);
                    openDrawer(record);
                  }}
                  highlightOnHover
                  rowStyle={(record) => ({
                    backgroundColor:
                      record.key === selectedRow?.key ? "#F6FAFD" : undefined,
                  })}
                />
                <div
                  ref={loadMoreSentinelRef}
                  style={{ height: 1, width: "100%" }}
                  aria-hidden
                />
                {loadingMore && (
                  <Center py="sm">
                    <Loader size="sm" type="oval" />
                  </Center>
                )}
              </div>
            </div>
          )}
          <Modal
            // zIndex={10000}
            title={
              modalState === ModalStates.ADD ? "Add Project" : "Edit Project"
            }
            opened={modalState !== ModalStates.OFF}
            onClose={handleCancel}
            withCloseButton={false}
            styles={{
              body: {
                // Ensure modal content has a z-index context
                position: "relative",
                zIndex: "auto",
              },
            }}
          >
            <ProcessCycleForm
              onSave={handleSaveProcessCycle}
              onCancel={handleCancel}
              onDelete={handleDeleteProcessCycle}
              initialState={modalState === ModalStates.ADD ? "ADD" : "EDIT"}
              selectedRow={selectedRow}
            />
          </Modal>
        </div>
        {selectedRow && (
          <div
            style={{
              flexBasis: "40%",
              flexGrow: 1,
              minWidth: 0,
              maxWidth: "40%",
              height: "100%",
              paddingRight: "16px",
              gap: "16px",
              flexDirection: "column",
              display: "flex",
              overflowY: "auto",
            }}
            className="mantine-wrapper"
          >
            <div
              className="title"
              style={{
                gap: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                {drawerTitle}
                <PriorityField priority={selectedRow?.priority ?? "None"} />
              </div>
              <IconCircleXFilled
                size={20}
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedRow(null)}
                color="var(--mantine-color-gray-6)"
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "16px",
              }}
            >
              <SelectProcessCycleStatus
                processCycleId={selectedCycleId}
                defaultValue={selectedCycleStatus + ""}
                onSelectStatus={onSelectProcessCycleStatus}
              />
              <DeadlineField deadline={selectedRow?.deadline ?? ""} />
            </div>

            {cycleTreeData && (
              <ProjectView
                processCycleId={cycleTreeData}
                checked={cycleTreeChecked}
                expanded={cycleTreeExpanded}
                refetch={refetch}
              />
            )}
          </div>
        )}
      </Group>
    </div>
  );
};

export default ProjectTable;
